{
  "app": {
    "name": "Pharmacy Inventory",
    "shortName": "PIMS",
    "tagline": "Multi-branch pharmacy inventory, POS and loyalty management",
    "language": "Language",
    "theme": "Theme",
    "light": "Light",
    "dark": "Dark",
    "system": "System",
    "loading": "Loading…",
    "skipToContent": "Skip to main content"
  },
  "nav": {
    "dashboard": "Dashboard",
    "pos": "Point of sale",
    "inventory": "Inventory",
    "medicines": "Medicines",
    "purchases": "Purchases",
    "customers": "Customers",
    "loyalty": "Loyalty cards",
    "reports": "Reports",
    "settings": "Settings",
    "sections": {
      "sell": "Sell",
      "stock": "Stock",
      "people": "People",
      "insights": "Insights"
    },
    "collapse": "Collapse sidebar",
    "expand": "Expand sidebar",
    "openMenu": "Open menu",
    "search": "Search or jump to…",
    "branch": "Branch",
    "organization": "Pharmacy",
    "signOut": "Sign out",
    "changePassword": "Change password",
    "account": "Account",
    "security": "Two-factor authentication"
  },
  "auth": {
    "welcome": "Welcome back",
    "signInHint": "Staff: use the username and password from your pharmacy owner. Owners: use your email.",
    "email": "Email",
    "loginId": "Username or email",
    "password": "Password",
    "signIn": "Sign in",
    "invalid": "Username/email or password is incorrect.",
    "trust": "Protected by two-factor authentication and row-level security.",
    "heroTitle": "Run every branch from one place",
    "heroBody": "Fast counter billing, expiry-first stock, loyalty cards and live reports — built for Bangladeshi pharmacies.",
    "heroPoints": [
      "Nearest-expiry stock is sold first, automatically",
      "Bangla and English, light and dark",
      "Owner sees every branch in real time"
    ],
    "mfaTitle": "Two-factor authentication",
    "mfaVerifyBody": "Enter the 6-digit code from your authenticator app.",
    "mfaEnrollBody": "Owners protect the pharmacy with an authenticator app. Scan this QR code with Google Authenticator or Microsoft Authenticator, then enter the 6-digit code.",
    "mfaSecret": "Can't scan? Enter this key manually",
    "code": "6-digit code",
    "verify": "Verify",
    "back": "Back",
    "codeInvalid": "That code didn't work. Check the time on your phone and try again."
  },
  "onboarding": {
    "title": "Set up your pharmacy",
    "body": "Create your pharmacy and its first branch. You can add more branches and staff later.",
    "pharmacyName": "Pharmacy name",
    "branchName": "First branch name",
    "branchCode": "Branch code",
    "branchCodeHint": "2–6 letters, printed on invoices (e.g. MPR → MPR-2627-000001).",
    "create": "Create pharmacy",
    "invitations": "You have been invited",
    "accept": "Accept",
    "noAccess": "Waiting for access",
    "noAccessBody": "Staff: ask the owner to add you. Owners: sign out and sign in again with your authenticator code.",
    "ownerMfa": "The owner account controls staff, prices and money, so it is protected with an authenticator app (Google Authenticator or Microsoft Authenticator). Set it up once; staff only need their password.",
    "setupMfa": "Set up authenticator and continue"
  },
  "dashboard": {
    "greeting": "Good {{part}}, {{name}}",
    "morning": "morning",
    "afternoon": "afternoon",
    "evening": "evening",
    "subtitle": "Here's how {{branch}} is doing today.",
    "todaySales": "Today's sales",
    "transactions": "Bills today",
    "avgBasket": "Average bill",
    "grossProfit": "Gross profit",
    "vsYesterday": "vs yesterday",
    "salesTrend": "Sales, last 14 days",
    "salesTrendBody": "Net sales after returns and voids",
    "expiring": "Expiring soon",
    "expiringBody": "Batches expiring in the next {{days}} days",
    "lowStock": "Low stock",
    "lowStockBody": "At or below reorder level",
    "daysLeft": "{{n}} days",
    "units": "{{n}} units",
    "noExpiring": "Nothing expiring soon",
    "noLowStock": "Stock levels look healthy",
    "newSale": "New sale",
    "hidden": "Owner only"
  },
  "pos": {
    "title": "Point of sale",
    "search": "Search medicine, generic or scan barcode",
    "searchHint": "Type at least 2 letters",
    "noResults": "No medicine found",
    "inStock": "{{n}} in stock",
    "outOfStock": "Out of stock",
    "exp": "Exp {{date}}",
    "cart": "Current bill",
    "emptyCart": "Bill is empty",
    "emptyCartBody": "Search for a medicine and press Enter to add it.",
    "qty": "Qty",
    "discount": "Disc %",
    "remove": "Remove {{name}}",
    "customer": "Customer / loyalty card",
    "customerPlaceholder": "Card number or mobile",
    "apply": "Apply",
    "member": "{{plan}} · {{discount}}% off · {{points}} pts",
    "subtotal": "Subtotal",
    "lineDiscounts": "Discounts",
    "estimated": "Estimated total",
    "estimateNote": "Final price, loyalty discount and batch allocation are calculated securely by the server.",
    "payment": "Payment",
    "tendered": "Amount received",
    "change": "Change",
    "complete": "Complete sale",
    "clear": "Clear bill",
    "prescription": "Prescription required",
    "prescriptionBody": "This bill contains controlled or prescription-only medicines.",
    "patientName": "Patient name",
    "doctorName": "Doctor name",
    "doctorReg": "BMDC registration no.",
    "prescriptionDate": "Prescription date",
    "success": "Sale completed",
    "invoice": "Invoice",
    "total": "Total",
    "paid": "Paid",
    "due": "Due",
    "pointsEarned": "Points earned",
    "print": "Print receipt",
    "newSale": "New sale",
    "thanks": "Thank you. Get well soon.",
    "shortcuts": "Shortcuts",
    "methods": {
      "cash": "Cash",
      "bkash": "bKash",
      "nagad": "Nagad",
      "rocket": "Rocket",
      "card": "Card",
      "bank_transfer": "Bank",
      "loyalty_points": "Points"
    },
    "schedule": {
      "otc": "OTC",
      "rx": "Rx",
      "controlled": "Controlled"
    },
    "loyaltyDiscount": "Loyalty discount",
    "rack": "Rack {{rack}}"
  },
  "inventory": {
    "title": "Inventory",
    "subtitle": "Stock, expiry and reorder alerts for {{branch}}",
    "batch": "Batch",
    "medicine": "Medicine",
    "expiry": "Expiry",
    "onHand": "On hand",
    "valueMrp": "Value (MRP)",
    "reorderLevel": "Reorder at",
    "sellable": "Sellable",
    "rack": "Rack",
    "stockOnHand": "Stock on hand",
    "filter": "Filter by name or rack",
    "noStock": "No stock in this branch yet",
    "noStockBody": "Load opening stock or receive a purchase from the Purchases page.",
    "nextExpiry": "Next expiry",
    "price": "Price",
    "batches_one": "{{n}} batch",
    "batches_other": "{{n}} batches"
  },
  "common": {
    "close": "Close",
    "cancel": "Cancel",
    "save": "Save",
    "pageOf": "Page {{page}} of {{pages}}",
    "previous": "Previous",
    "next": "Next"
  },
  "medicines": {
    "title": "Medicines",
    "subtitle": "{{n}} medicines in your catalog",
    "add": "Add medicine",
    "addFirst": "Add your first medicine",
    "edit": "Edit medicine",
    "editNamed": "Edit {{name}}",
    "dialogBody": "The catalog is shared by all branches. Rack and reorder level are saved for {{branch}}.",
    "brandName": "Brand name",
    "strength": "Strength",
    "generic": "Generic name",
    "manufacturer": "Company",
    "form": "Form",
    "unit": "Selling unit",
    "unitHint": "Stock and prices are counted per this unit.",
    "rack": "Rack / shelf",
    "rackHint": "Shelf here, e.g. A-3",
    "schedule": "Type",
    "schedules": {
      "otc": "OTC",
      "rx": "Prescription (Rx)",
      "controlled": "Controlled"
    },
    "scheduleHint": {
      "otc": "Can be sold without a prescription.",
      "rx": "Prescription-only medicine.",
      "controlled": "Narcotic or psychotropic: the POS asks for prescription details."
    },
    "reorderLevel": "Reorder level",
    "reorderHint": "Warn when stock falls to this",
    "wholeNumber": "Enter a whole number",
    "barcodes": "Barcodes",
    "barcodesHint": "Scan or type; separate several with commas.",
    "sku": "SKU / code",
    "notes": "Notes",
    "loyaltyEligible": "Loyalty discount applies",
    "active": "Active (can be sold)",
    "inactive": "Inactive",
    "required": "Required",
    "created": "{{name}} added",
    "updated": "{{name}} updated",
    "search": "Search brand, generic or SKU",
    "showInactive": "Show inactive",
    "noMatch": "No medicine matches your search",
    "empty": "No medicines yet",
    "emptyBody": "Add the medicines you sell. Then load opening stock or receive a purchase in Purchases.",
    "medicine": "Medicine",
    "perUnit": "per {{unit}}",
    "forms": {
      "tablet": "Tablet",
      "capsule": "Capsule",
      "syrup": "Syrup",
      "suspension": "Suspension",
      "solution": "Solution",
      "injection": "Injection",
      "infusion": "Infusion (IV)",
      "drops": "Drops",
      "cream": "Cream",
      "ointment": "Ointment",
      "gel": "Gel",
      "lotion": "Lotion",
      "inhaler": "Inhaler",
      "nebuliser_solution": "Nebuliser solution",
      "powder": "Powder",
      "sachet": "Sachet",
      "suppository": "Suppository",
      "spray": "Spray",
      "patch": "Patch",
      "device": "Device",
      "other": "Other"
    }
  },
  "purchases": {
    "subtitle": "Receive supplier invoices and load stock into {{branch}}",
    "tabs": {
      "receive": "Receive goods",
      "opening": "Opening stock",
      "history": "History"
    },
    "noAccess": "Purchases are restricted",
    "noAccessBody": "Purchase prices are visible to owners, managers and accountants only.",
    "supplier": "Supplier",
    "chooseSupplier": "Choose supplier…",
    "newSupplier": "New supplier",
    "supplierName": "Supplier name",
    "contactPerson": "Contact person",
    "phone": "Mobile",
    "address": "Address",
    "supplierCreated": "Supplier {{name}} added",
    "invoiceNo": "Supplier invoice no.",
    "invoiceDate": "Invoice date",
    "items": "Items received",
    "openingItems": "Stock on the shelf now",
    "addMedicine": "Add medicine: type a name or scan a barcode",
    "noItems": "No items yet",
    "noItemsBody": "Search above to add each medicine on the supplier's invoice.",
    "openingBody": "Count what is on the shelves today and enter it batch by batch. Do this once when the branch starts using the system.",
    "batchNo": "Batch",
    "expiry": "Expiry",
    "qty": "Qty",
    "bonus": "Bonus",
    "extra": "Extra",
    "unitCost": "Cost",
    "mrp": "MRP",
    "salePrice": "Sale price",
    "lineTotal": "Line total",
    "perUnit": "Qty and prices per {{unit}}",
    "discount": "Invoice discount",
    "paidNow": "Paid now",
    "paidHint": "Leave 0 if bought on credit.",
    "openingNote": "Cost is your purchase price per unit; it is used for profit reports and is never shown to salespeople.",
    "lines": "Lines",
    "dueToSupplier": "Due to supplier",
    "saveReceipt": "Save goods receipt",
    "saveOpening": "Save opening stock",
    "discard": "Discard draft",
    "openingSaved": "Opening stock saved ({{n}} batches)",
    "received": "Goods receipt {{no}} saved · {{total}}",
    "noReceipts": "No goods receipts yet",
    "receiptNo": "Receipt",
    "date": "Date",
    "paidInFull": "Paid",
    "err": {
      "batch": "Required",
      "expiry": "Pick month",
      "expired": "Already expired",
      "quantity": "Whole number > 0",
      "bonus": "Whole number",
      "money": "Amount like 12.50",
      "aboveMrp": "Above MRP",
      "supplier": "Choose a supplier",
      "futureDate": "Cannot be in the future",
      "duplicate": "Same batch added twice",
      "discount": "Between 0 and subtotal",
      "paid": "Between 0 and total"
    }
  },
  "customers": {
    "subtitle": "{{n}} customers",
    "add": "Add customer",
    "edit": "Edit customer",
    "editNamed": "Edit {{name}}",
    "dialogBody": "Customers are shared by all branches. A mobile number is needed for a loyalty card.",
    "name": "Customer name",
    "phoneHint": "Used to find the customer and for loyalty cards.",
    "creditLimit": "Credit limit (বাকি)",
    "creditHint": "0 = no credit sales.",
    "active": "Active",
    "created": "{{name}} added",
    "updated": "{{name}} updated",
    "search": "Search name or mobile",
    "noMatch": "No customer matches",
    "empty": "No customers yet",
    "emptyBody": "Add regular customers to sell on credit and give them loyalty cards.",
    "due": "Due",
    "noCard": "No card"
  },
  "loyalty": {
    "subtitle": "Membership cards with discounts and points, valid in every branch",
    "tabs": {
      "members": "Members",
      "plans": "Plans"
    },
    "filter": "Filter members",
    "filters": {
      "active": "Active",
      "expiring": "Expiring soon",
      "expired": "Expired",
      "all": "All"
    },
    "states": {
      "active": "Active",
      "expiring": "Expiring soon",
      "upcoming": "Starts later",
      "expired": "Expired",
      "cancelled": "Cancelled"
    },
    "searchMembers": "Search name, mobile or card no.",
    "enroll": "Enroll customer",
    "enrollBody": "Issue a loyalty card or renew a membership. A renewal starts the day after the current one ends.",
    "enrollPay": "Enroll · collect {{fee}}",
    "enrollNamed": "Give {{name}} a loyalty card",
    "renewNamed": "Renew {{name}}'s card",
    "replaceNamed": "Replace {{name}}'s card",
    "cancelNamed": "Cancel {{name}}'s membership",
    "customer": "Customer",
    "findCustomer": "Find customer by name or mobile",
    "noPhone": "No mobile number",
    "change": "Change",
    "plan": "Plan",
    "planSummary": "{{fee}} · {{discount}}% off · {{months}} months",
    "cardNo": "Card number",
    "cardNoHint": "Leave empty to generate one, or type a pre-printed card number.",
    "autoNumber": "Auto-generate",
    "newCardNo": "New card number",
    "noActivePlan": "No plan is offered yet. The owner sets fee and benefits in Loyalty → Plans.",
    "enrolled": "Card ready",
    "validTill": "Valid till {{date}}",
    "feeCollected": "Collect {{fee}} from the customer.",
    "done": "Done",
    "validity": "Validity",
    "noMembers": "No members here",
    "noMembersBody": "Enroll a customer to issue the first loyalty card.",
    "cardReplaced": "Replaced",
    "reason": "Reason",
    "cancelTitle": "Cancel membership",
    "cancelBody": "{{name}} will lose the discount immediately. The fee is not refunded automatically.",
    "cancelConfirm": "Cancel membership",
    "cancelled": "Membership cancelled",
    "replaceTitle": "Replace lost or damaged card",
    "replaceBody": "{{name}}'s old card stops working; membership and points move to the new card.",
    "replaceConfirm": "Issue new card",
    "replacedToast": "New card {{no}} issued",
    "programme": "Loyalty programme",
    "programmeBody": "When off, no card gives a discount or earns points, and no one can enroll.",
    "programmeOn": "Programme on",
    "planOn": "Offered",
    "planOff": "Not offered",
    "planName": "Plan name",
    "months": "Months",
    "fee": "Card fee",
    "discount": "Discount",
    "discountHint": "On loyalty-eligible medicines, max 50%.",
    "maxDiscount": "Max discount per bill",
    "optional": "Optional",
    "pointsPer100": "Points per ৳100",
    "pointsHint": "0 = no points.",
    "pointValue": "Value of 1 point",
    "minRedeem": "Min points to redeem",
    "offerPlan": "Offer this plan to customers",
    "planSaved": "{{name}} saved",
    "addPlan": "Add plan",
    "newPlanName": "Plan {{n}}",
    "ownerOnly": "Only the owner can change plans.",
    "err": {
      "name": "2–60 characters",
      "months": "1 to 36",
      "discount": "0 to 50",
      "points": "0 to 1000",
      "fix": "Fix the highlighted fields."
    }
  },
  "settings": {
    "subtitle": "Staff, access, branches and pharmacy rules",
    "tabs": {
      "staff": "Staff & access",
      "branches": "Branches",
      "pharmacy": "Pharmacy"
    },
    "noAccess": "Only the owner can change settings.",
    "staff": "Staff",
    "addStaff": "Add staff",
    "addStaffBody": "Creates a username and password for this person. No email is needed: give them the username and password in person.",
    "username": "Username",
    "usernameHint": "3–30 small letters or numbers, e.g. rafiq or rafiq.mpr. Must be unique.",
    "usernameInvalid": "Use 3–30 small letters, numbers, dot, dash or underscore.",
    "fullName": "Full name",
    "email": "Email",
    "role": "Role",
    "tempPassword": "Temporary password",
    "passwordHint": "10+ characters with upper, lower case and a number.",
    "passwordWeak": "Use 10+ characters with upper, lower case and a number.",
    "generate": "Generate a new password",
    "branches": "Branches",
    "pickBranch": "Choose at least one branch.",
    "allBranches": "This role works in every branch.",
    "fineTuneLater": "You can fine-tune exactly what they may do after they first sign in (Manage).",
    "staffAdded": "Staff added",
    "loginCreated": "A sign-in was created. Username: {{username}}.",
    "loginExists": "The username {{username}} is already used. Choose another one, for example {{username}}.{{branch}}.",
    "loginUnavailable": "Invitation created for {{username}}, but the sign-in could not be created automatically (the admin-users function is not deployed). Create the user in Supabase → Authentication → Add user with the email {{email}} and Auto Confirm, then they sign in with the username.",
    "copy": "Copy username and password",
    "handOver": "Give these to the person privately. They can change the password from the account menu after signing in.",
    "next1": "They open the app and sign in with this username and password.",
    "next2": "They join your pharmacy automatically at their first sign-in (within 3 days).",
    "next3": "Forgotten password? Open Manage → Reset password.",
    "person": "Person",
    "lastSignIn": "Last sign-in",
    "never": "Never",
    "all": "All branches",
    "custom": "Custom",
    "manage": "Manage",
    "manageNamed": "Manage {{name}}",
    "notSelf": "You cannot change your own access here.",
    "pending": "Pending invitations",
    "noPending": "No pending invitations",
    "expires": "Expires {{date}}",
    "revoke": "Revoke",
    "resetPassword": "Reset password",
    "resetPasswordBody": "Sets a new temporary password. The old password stops working at once.",
    "newPasswordFor": "New password for {{name}}",
    "resetOnlyStaff": "Only staff with a username can have their password reset here.",
    "accessHint": "Starts from the role. Tick or untick to give or take away individual permissions.",
    "resetToRole": "Reset to role defaults",
    "ownerAll": "Owners always have full access.",
    "saveAccess": "Save access",
    "accessSaved": "Access for {{name}} saved",
    "groups": {
      "sales": "Sales",
      "stock": "Medicines & stock",
      "purchases": "Purchases",
      "customers": "Customers",
      "loyalty": "Loyalty cards",
      "reports": "Reports"
    },
    "perms": {
      "sales.create": "Sell at the counter (POS)",
      "sales.credit": "Sell on credit (বাকি)",
      "sales.void": "Void a sale",
      "sales.return": "Process customer returns",
      "catalog.manage": "Add and edit medicines",
      "pricing.manage": "Change selling prices",
      "stock.adjust": "Adjust stock / opening stock",
      "purchases.view": "See purchases and supplier prices",
      "purchases.receive": "Receive goods from suppliers",
      "purchases.return": "Return goods to suppliers",
      "suppliers.manage": "Add and edit suppliers",
      "suppliers.pay": "Pay suppliers",
      "customers.manage": "Add and edit customers",
      "customers.collect": "Collect customer dues",
      "loyalty.enroll": "Enroll and renew loyalty cards",
      "loyalty.cancel": "Cancel or replace loyalty cards",
      "loyalty.manage_plans": "Edit loyalty plans",
      "reports.view": "View reports and dashboard figures",
      "reports.view_cost": "See cost and profit",
      "data.export": "Export data (CSV)",
      "controlled.register.view": "Controlled-drug register",
      "audit.view": "Audit log"
    },
    "roles": {
      "owner": "Owner",
      "manager": "Branch manager",
      "salesman": "Salesperson",
      "accountant": "Accountant",
      "auditor": "Auditor"
    },
    "roleHints": {
      "owner": "Full access to everything, including staff and settings.",
      "manager": "Runs assigned branches: sales, stock, purchases, reports.",
      "salesman": "Sells at the counter, customers and loyalty cards.",
      "accountant": "All branches: reports, purchases, payments. No selling.",
      "auditor": "All branches, read-only: reports and audit."
    },
    "addBranch": "Add branch",
    "editBranch": "Edit branch",
    "editNamed": "Edit {{name}}",
    "branchBody": "Each branch keeps its own stock, invoices and staff.",
    "branchName": "Branch name",
    "codeFixed": "The code is printed on invoices and cannot change.",
    "codeInvalid": "2–6 letters or digits",
    "branchOpen": "Branch is open",
    "branchSaved": "{{name}} saved",
    "closed": "Closed",
    "general": "General",
    "vat": "VAT",
    "vatHint": "Included in prices; 0 if you are not VAT-registered.",
    "selling": "Selling rules",
    "salesmanDiscount": "Max discount: salesperson",
    "managerDiscount": "Max discount: manager",
    "discountHint": "Per line, above this needs a higher role.",
    "rounding": "Cash rounding",
    "roundingNone": "No rounding",
    "roundingTaka": "Round to nearest taka",
    "returnDays": "Return window",
    "returnHint": "Days a sale can be returned.",
    "voidHours": "Void window",
    "voidHint": "Hours a sale can be voided.",
    "rxNeeded": "Require prescription details for Rx medicines",
    "expiry": "Expiry",
    "blockDays": "Stop selling before expiry",
    "blockHint": "0 = only expired batches are blocked.",
    "alertDays": "Expiry alert",
    "alertHint": "Show batches expiring within this many days.",
    "days": "days",
    "hours": "hours",
    "saveSettings": "Save settings",
    "saved": "Settings saved",
    "outOfRange": "Out of range",
    "loginUnavailableShort": "The admin-users function is not deployed.",
    "remove": "Remove from pharmacy",
    "removeBody": "Removes this person's access. Their past sales and records stay. You can restore them later.",
    "removeConfirm": "Remove {{name}}? They can no longer sign in to this pharmacy.",
    "removeYes": "Yes, remove",
    "removed": "{{name}} removed",
    "restored": "{{name}} restored",
    "removedStaff": "Removed staff",
    "restore": "Restore",
    "restoreNamed": "Restore {{name}}"
  },
  "reports": {
    "subtitle": "Sales, profit, stock value and expiry",
    "noAccess": "You do not have access to reports.",
    "tabs": {
      "sales": "Sales",
      "stock": "Stock value",
      "expiry": "Expiry",
      "low": "Low stock"
    },
    "period": "Period",
    "presets": {
      "today": "Today",
      "yesterday": "Yesterday",
      "last7": "Last 7 days",
      "thisMonth": "This month",
      "lastMonth": "Last month",
      "custom": "Custom"
    },
    "from": "From",
    "to": "To",
    "badRange": "End must be after start",
    "allBranches": "All branches",
    "exportCsv": "Export CSV",
    "totals": "Totals",
    "netSales": "Net sales",
    "bills": "Bills",
    "discounts": "Discounts",
    "returns": "Returns",
    "grossProfit": "Gross profit",
    "byDay": "By day",
    "byBranch": "By branch",
    "noSales": "No sales in this period",
    "skus": "Medicines",
    "units": "Units",
    "valueCost": "Value (cost)",
    "expiredUnits": "Expired units",
    "total": "Total",
    "within": "Expiring within",
    "daysN": "{{n}} days"
  },
  "comingSoon": {
    "title": "Coming in the next milestone",
    "body": "The database for this module is ready and tested. Its screens are being built next."
  },
  "errors": {
    "unknown": "Something went wrong. Please try again.",
    "network": "You appear to be offline. Check the internet connection.",
    "forbidden": "You don't have permission to do that.",
    "mfa_required": "Verify with two-factor authentication to continue.",
    "not_authenticated": "Please sign in again.",
    "insufficient_stock": "Not enough sellable stock.",
    "discount_limit": "This discount is above your limit. Ask a manager.",
    "credit_limit": "This would exceed the customer's credit limit.",
    "customer_required": "Select a customer to sell on credit.",
    "prescription_required": "Prescription details are required for this bill.",
    "loyalty_inactive": "This loyalty card has no active membership today.",
    "overpayment": "Card and mobile payments cannot exceed the total.",
    "invalid_phone": "Enter a valid Bangladeshi mobile number (01XXXXXXXXX).",
    "duplicate_medicine": "This medicine (same brand, form, strength and company) already exists.",
    "duplicate_sku": "Another medicine already uses this SKU.",
    "duplicate_barcode": "This barcode already belongs to another medicine.",
    "invalid_barcode": "Barcodes are 4–64 letters, digits or dashes.",
    "invalid_medicine_field": "One of the fields is too long or invalid.",
    "duplicate_supplier": "A supplier with this name already exists.",
    "invalid_name": "Name is required.",
    "not_found": "Not found. It may have been removed.",
    "expired_batch": "Expiry date must be in the future.",
    "price_above_mrp": "Sale price cannot be above MRP.",
    "invalid_price": "MRP must be greater than zero.",
    "invalid_cost": "Cost cannot be negative.",
    "invalid_quantity": "Quantity must be a whole number greater than zero.",
    "invalid_batch": "Batch number is required.",
    "duplicate_items": "The same medicine batch appears twice.",
    "invalid_discount": "Discount must be between zero and the subtotal.",
    "invalid_payment": "Paid amount must be between zero and the total.",
    "invalid_date": "Invoice date cannot be in the future.",
    "invalid_supplier": "Supplier not found or inactive.",
    "invalid_medicine": "Medicine not found or inactive.",
    "23505": "This already exists (for example, the same supplier invoice number was entered before).",
    "duplicate_phone": "Another customer already has this mobile number.",
    "phone_required": "A mobile number is required for a loyalty card. Edit the customer first.",
    "loyalty_disabled": "The loyalty programme is turned off (Loyalty → Plans).",
    "invalid_plan": "This plan is not offered.",
    "invalid_customer": "Customer not found or inactive.",
    "invalid_card_no": "Card number is not valid (check digit).",
    "card_mismatch": "This customer already has a different active card.",
    "renewal_pending": "A renewal is already waiting to start for this card.",
    "already_cancelled": "This membership is already cancelled.",
    "reason_required": "Please give a reason.",
    "invalid_email": "Enter a valid email address.",
    "already_member": "This person is already a member.",
    "invalid_role": "Choose a role.",
    "invalid_branch": "Choose branches of this pharmacy.",
    "last_owner": "The pharmacy must keep at least one active owner.",
    "owner_has_all": "Owners always have full access.",
    "not_grantable": "That permission is reserved for owners.",
    "permission_dependency": "Seeing purchases also shows purchase prices: allow cost and profit too.",
    "invalid_permissions": "Invalid permissions.",
    "no_invitation": "Invite the person first (no pending invitation found).",
    "not_allowed": "Only the owner can do this.",
    "owner_password": "Owners change their own password.",
    "not_staff_login": "This person does not sign in with a username of this pharmacy.",
    "password_length": "Password must be 10–72 characters.",
    "password_weak": "Password needs upper, lower case and a number.",
    "invalid_range": "Choose a range of at most 400 days.",
    "duplicate_branch": "A branch with this code already exists.",
    "reload": "Reload"
  },
  "account": {
    "title": "Change password",
    "new": "New password",
    "confirm": "Repeat new password",
    "mismatch": "The passwords do not match.",
    "saved": "Password changed"
  }
}
